<template>
  <div>
    <div class="px-4 sm:px-0">
      <h3 class="text-2xl font-semibold text-white mb-4">Documents destinés aux membres</h3>
    </div>
    <div class="bg-gray-900 p-6 rounded-xl border-gray-700 border">
      <div class="col-span-full mb-8">
        <label for="folder-name" class="block text-sm/6 font-medium text-white">Créer un dossier</label>
        <div class="mt-2 flex gap-3">
          <input
            id="folder-name"
            v-model="newFolderName"
            type="text"
            placeholder="Ex: Bulletin 2026"
            class="px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"
          />
          <button type="button" @click="createFolder" class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600">Créer</button>
        </div>
        <div class="mt-3 flex flex-wrap gap-2" v-if="folders.length">
          <div v-for="folder in folders" :key="`manage-folder-${folder.id}`" class="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs text-white">
            <span>{{ folder.name }}</span>
            <button type="button" @click="deleteFolder(folder)" class="text-red-300 hover:text-red-200">Supprimer</button>
          </div>
        </div>
      </div>

      <div class="col-span-full">
        <label for="file-upload" class="block text-sm/6 font-medium text-white">Sélectionner un fichier</label>
        <div class="mt-2 flex flex-wrap gap-2 rounded-md bg-white/5 p-3 ring-1 ring-inset ring-white/10">
          <button
            type="button"
            @click="selectedFileFolderId = ''"
            :class="[
              'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
              selectedFileFolderId === '' ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20'
            ]"
          >
            Sans dossier
          </button>
          <button
            v-for="folder in folders"
            :key="`file-folder-${folder.id}`"
            type="button"
            @click="selectedFileFolderId = String(folder.id)"
            :class="[
              'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
              selectedFileFolderId === String(folder.id) ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20'
            ]"
          >
            {{ folder.name }}
          </button>
        </div>
        <label for="file-upload" class="mt-2 flex justify-center rounded-lg border border-dashed border-gray-200/25 px-6 py-10 cursor-pointer">
          <div class="text-center">
            <svg class="mx-auto size-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 4h24l8 8v32H6V4z" />
              <line x1="12" y1="20" x2="32" y2="20" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></line>
              <line x1="12" y1="26" x2="28" y2="26" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></line>
              <line x1="12" y1="32" x2="24" y2="32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></line>
            </svg>
            <div class="mt-4 flex text-sm/6 text-gray-600">
              <label for="file-upload" class="relative cursor-pointer rounded-md font-semibold text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-green-600 focus-within:ring-offset-2 focus-within:ring-offset-gray-900 hover:text-green-500">
                <span>Choisir un fichier</span>
                <input id="file-upload" name="file-upload" type="file" class="sr-only" @change="handleFileUpload" />
              </label>
              <p class="pl-1">ou glisser-déposer ici</p>
            </div>
            <p class="text-xs/5 text-gray-600">PDF, jusqu'à 50MB</p>
          </div>
        </label>
        <div class="w-full flex justify-end mt-4">
          <button type="button" @click="submitFile" class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600">Ajouter</button>
        </div>
      </div>

      <!-- Input pour ajouter une URL -->
      <div class="col-span-full mt-6">
        <label for="url-input" class="block text-sm/6 font-medium text-white">Ajouter un lien URL</label>
        <div class="mt-2 flex flex-wrap gap-2 rounded-md bg-white/5 p-3 ring-1 ring-inset ring-white/10">
          <button
            type="button"
            @click="selectedUrlFolderId = ''"
            :class="[
              'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
              selectedUrlFolderId === '' ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20'
            ]"
          >
            Sans dossier
          </button>
          <button
            v-for="folder in folders"
            :key="`url-folder-${folder.id}`"
            type="button"
            @click="selectedUrlFolderId = String(folder.id)"
            :class="[
              'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
              selectedUrlFolderId === String(folder.id) ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20'
            ]"
          >
            {{ folder.name }}
          </button>
        </div>
        <div class="mt-2">
          <input
            type="text"
            id="url-input"
            v-model="newLinkUrl"
            placeholder="Entrez l'URL"
            class="mt-2 px-4 py-4 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"
          />
        </div>
        <div class="w-full flex justify-end mt-4">
          <button type="button" @click="addUrlLink" class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600">Ajouter le lien</button>
        </div>
      </div>
    </div>

    <!-- Affichage des liens et fichiers téléchargés -->
    <div class="mt-16 border-t border-white/10 py-28">
      <dl class="divide-y divide-white/10">
        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
          <dt class="text-sm/6 font-medium text-white">Tous les liens</dt>
          <dd class="mt-2 text-sm text-white sm:col-span-2 sm:mt-0 space-y-4">
            <div v-for="group in groupedLinks" :key="`links-${group.name}`">
              <p class="mb-2 text-sm font-semibold text-green-400">{{ group.name }}</p>
              <ul role="list" class="divide-y divide-white/10 rounded-md border border-white/20">
                <template v-for="link in group.items" :key="`row-link-${link.id}`">
                  <li class="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6">
                    <div class="flex w-0 flex-1 items-center">
                      <PaperClipIcon class="size-5 shrink-0 text-gray-400" aria-hidden="true" />
                      <div class="ml-4 flex min-w-0 flex-1 gap-2">
                        <span class="truncate font-medium">{{ link.url_lien }}</span>
                        <span class="text-xs text-gray-400">{{ formatDate(link.created_at) }}</span>
                      </div>
                    </div>
                    <div class="ml-4 shrink-0 items-center flex gap-4">
                      <a :href="link.href" class="font-medium text-green-400 hover:text-green-300">Accéder</a>
                      <button @click="openFolderEditor('link', link)" class="font-medium text-indigo-400 hover:text-indigo-300">Associer</button>
                      <button @click="deleteUrlLink(link)">
                        <TrashIcon class="w-5 h-5 shrink-0 text-gray-400 hover:text-red-400" aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                  <li v-if="isEditingItem('link', link.id)" class="border-t border-white/10 px-4 py-3">
                    <div class="flex flex-wrap gap-2 rounded-md bg-white/5 p-3 ring-1 ring-inset ring-white/10">
                      <button type="button" @click="editingFolderId = ''" :class="['rounded-full px-3 py-1.5 text-xs font-medium transition-colors', editingFolderId === '' ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20']">Sans dossier</button>
                      <button v-for="folder in folders" :key="`move-link-${link.id}-${folder.id}`" type="button" @click="editingFolderId = String(folder.id)" :class="['rounded-full px-3 py-1.5 text-xs font-medium transition-colors', editingFolderId === String(folder.id) ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20']">{{ folder.name }}</button>
                    </div>
                    <div class="mt-3 flex justify-end gap-2">
                      <button type="button" @click="closeFolderEditor" class="rounded-md bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20">Annuler</button>
                      <button type="button" @click="applyFolderToLink(link)" class="rounded-md bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-500">Valider</button>
                    </div>
                  </li>
                </template>
              </ul>
            </div>
          </dd>
        </div>
      </dl>
    </div>

    <!-- Affichage des fichiers téléchargés (PDF) -->
    <div class="mt-16 border-t border-white/10 py-28">
      <dl class="divide-y divide-white/10">
        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
          <dt class="text-sm/6 font-medium text-white">Tous les fichiers</dt>
          <dd class="mt-2 text-sm text-white sm:col-span-2 sm:mt-0 space-y-4">
            <div v-for="group in groupedMembers" :key="`files-${group.name}`">
              <p class="mb-2 text-sm font-semibold text-green-400">{{ group.name }}</p>
              <ul role="list" class="divide-y divide-white/10 rounded-md border border-white/20">
                <template v-for="member in group.items" :key="`row-member-${member.id}`">
                  <li class="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6">
                    <div class="flex w-0 flex-1 items-center">
                      <PaperClipIcon class="size-5 shrink-0 text-gray-400" aria-hidden="true" />
                      <div class="ml-4 flex min-w-0 flex-1 gap-2">
                        <span class="truncate font-medium">{{ member.title }}</span>
                        <span class="text-xs text-gray-400">{{ formatDate(member.created_at) }}</span>
                      </div>
                    </div>
                    <div class="ml-4 shrink-0 items-center flex gap-4">
                      <a :href="member.href" class="font-medium text-green-400 hover:text-green-300">Télécharger</a>
                      <button @click="openFolderEditor('member', member)" class="font-medium text-indigo-400 hover:text-indigo-300">Associer</button>
                      <button @click="deletePdfFile(member)">
                        <TrashIcon class="w-5 h-5 shrink-0 text-gray-400 hover:text-red-400" aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                  <li v-if="isEditingItem('member', member.id)" class="border-t border-white/10 px-4 py-3">
                    <div class="flex flex-wrap gap-2 rounded-md bg-white/5 p-3 ring-1 ring-inset ring-white/10">
                      <button type="button" @click="editingFolderId = ''" :class="['rounded-full px-3 py-1.5 text-xs font-medium transition-colors', editingFolderId === '' ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20']">Sans dossier</button>
                      <button v-for="folder in folders" :key="`move-member-${member.id}-${folder.id}`" type="button" @click="editingFolderId = String(folder.id)" :class="['rounded-full px-3 py-1.5 text-xs font-medium transition-colors', editingFolderId === String(folder.id) ? 'bg-green-600 text-white' : 'bg-white/10 text-gray-200 hover:bg-white/20']">{{ folder.name }}</button>
                    </div>
                    <div class="mt-3 flex justify-end gap-2">
                      <button type="button" @click="closeFolderEditor" class="rounded-md bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20">Annuler</button>
                      <button type="button" @click="applyFolderToPdf(member)" class="rounded-md bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-500">Valider</button>
                    </div>
                  </li>
                </template>
              </ul>
            </div>
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { PaperClipIcon, TrashIcon } from '@heroicons/vue/20/solid'

const links = ref([])

const members = ref([])
const folders = ref([])

const selectedFile = ref(null)
const newLinkUrl = ref('')
const newFolderName = ref('')
const selectedFileFolderId = ref('')
const selectedUrlFolderId = ref('')
const editingTarget = ref(null)
const editingFolderId = ref('')

const getGroupName = (item) => item.folder_name || 'Sans dossier'

const sortByNewestDate = (a, b) => {
  const dateA = new Date(a.created_at || 0).getTime()
  const dateB = new Date(b.created_at || 0).getTime()
  return dateB - dateA
}

const groupedLinks = computed(() => {
  const grouped = {}

  for (const link of links.value) {
    const groupName = getGroupName(link)
    if (!grouped[groupName]) grouped[groupName] = []
    grouped[groupName].push(link)
  }

  return Object.keys(grouped)
    .map(name => {
      const items = grouped[name].sort(sortByNewestDate)
      const latestAt = items.length ? new Date(items[0].created_at || 0).getTime() : 0
      return { name, items, latestAt }
    })
    .sort((a, b) => {
      if (b.latestAt !== a.latestAt) return b.latestAt - a.latestAt
      return a.name.localeCompare(b.name, 'fr')
    })
})

const groupedMembers = computed(() => {
  const grouped = {}

  for (const member of members.value) {
    const groupName = getGroupName(member)
    if (!grouped[groupName]) grouped[groupName] = []
    grouped[groupName].push(member)
  }

  return Object.keys(grouped)
    .map(name => {
      const items = grouped[name].sort(sortByNewestDate)
      const latestAt = items.length ? new Date(items[0].created_at || 0).getTime() : 0
      return { name, items, latestAt }
    })
    .sort((a, b) => {
      if (b.latestAt !== a.latestAt) return b.latestAt - a.latestAt
      return a.name.localeCompare(b.name, 'fr')
    })
})

const formatDate = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('fr-FR')
}

const fetchFolders = async () => {
  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/folder')

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des dossiers')
    }

    folders.value = await response.json()
  } catch (error) {
    console.error(error)
  }
}

const fetchPdfFiles = async () => {
  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/pdf')
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des fichiers PDF')
    }
    const data = await response.json()

    members.value = data.map(item => ({
      title: item.url_image.split('/').pop(),
      href: item.url_image,
      id: item.id,
      folder_id: item.folder_id,
      folder_name: item.folder_name,
      created_at: item.created_at,
    }))
  } catch (error) {
    console.error(error)
  }
}

const fetchUrlLinks = async () => {
  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/url')
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des liens URL')
    }
    const data = await response.json()

    links.value = data.map(item => ({
      url_lien: item.url_lien,
      href: item.url_lien,
      id: item.id,
      folder_id: item.folder_id,
      folder_name: item.folder_name,
      created_at: item.created_at,
    }))
  } catch (error) {
    console.error(error)
  }
}

onMounted(() => {
  fetchFolders()
  fetchPdfFiles()
  fetchUrlLinks()
})

const openFolderEditor = (type, item) => {
  editingTarget.value = { type, id: item.id }
  editingFolderId.value = item.folder_id ? String(item.folder_id) : ''
}

const closeFolderEditor = () => {
  editingTarget.value = null
  editingFolderId.value = ''
}

const isEditingItem = (type, id) => {
  return editingTarget.value?.type === type && editingTarget.value?.id === id
}

const applyFolderToLink = async (link) => {
  try {
    const response = await fetch(`https://api.meritemaritime-fnmm.com/member/url/${link.id}/folder`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ folder_id: editingFolderId.value || null }),
    })

    if (!response.ok) {
      throw new Error('Erreur lors de l\'association du lien au dossier.')
    }

    const data = await response.json()
    const index = links.value.findIndex(l => l.id === link.id)
    if (index !== -1) {
      links.value[index] = {
        ...links.value[index],
        folder_id: data.folder_id || null,
        folder_name: data.folder_name || null,
      }
    }

    closeFolderEditor()
    alert('Lien associé au dossier avec succès!')
  } catch (error) {
    console.error(error)
    alert('Une erreur est survenue lors de l\'association du lien.')
  }
}

const applyFolderToPdf = async (member) => {
  try {
    const response = await fetch(`https://api.meritemaritime-fnmm.com/member/pdf/${member.id}/folder`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ folder_id: editingFolderId.value || null }),
    })

    if (!response.ok) {
      throw new Error('Erreur lors de l\'association du fichier au dossier.')
    }

    const data = await response.json()
    const index = members.value.findIndex(m => m.id === member.id)
    if (index !== -1) {
      members.value[index] = {
        ...members.value[index],
        folder_id: data.folder_id || null,
        folder_name: data.folder_name || null,
      }
    }

    closeFolderEditor()
    alert('Fichier associé au dossier avec succès!')
  } catch (error) {
    console.error(error)
    alert('Une erreur est survenue lors de l\'association du fichier.')
  }
}

const createFolder = async () => {
  const folderName = newFolderName.value.trim()

  if (!folderName) {
    alert('Veuillez entrer un nom de dossier.')
    return
  }

  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/folder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: folderName }),
    })

    if (response.status === 409) {
      alert('Ce dossier existe déjà.')
      return
    }

    if (!response.ok) {
      throw new Error('Erreur lors de la creation du dossier.')
    }

    const folder = await response.json()
    folders.value.push(folder)
    folders.value.sort((a, b) => a.name.localeCompare(b.name, 'fr'))
    newFolderName.value = ''
    alert('Dossier créé avec succès!')
  } catch (error) {
    console.error(error)
    alert('Une erreur est survenue lors de la creation du dossier.')
  }
}

const deleteFolder = async (folder) => {
  if (!confirm(`Supprimer le dossier "${folder.name}" ? Les contenus seront replacés dans "Sans dossier".`)) {
    return
  }

  try {
    const response = await fetch(`https://api.meritemaritime-fnmm.com/member/folder/${folder.id}`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw new Error('Erreur lors de la suppression du dossier.')
    }

    folders.value = folders.value.filter(f => f.id !== folder.id)
    links.value = links.value.map(link => link.folder_id === folder.id ? { ...link, folder_id: null, folder_name: null } : link)
    members.value = members.value.map(member => member.folder_id === folder.id ? { ...member, folder_id: null, folder_name: null } : member)
    alert('Dossier supprimé avec succès!')
  } catch (error) {
    console.error(error)
    alert('Une erreur est survenue lors de la suppression du dossier.')
  }
}

const handleFileUpload = (event) => {
  selectedFile.value = event.target.files[0]
}

const submitFile = async () => {
  if (!selectedFile.value) {
    alert('Veuillez sélectionner un fichier avant de soumettre.')
    return
  }

  const formData = new FormData()
  formData.append('file', selectedFile.value)
  if (selectedFileFolderId.value) {
    formData.append('folder_id', selectedFileFolderId.value)
  }

  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/pdf', {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error('Erreur lors de l\'upload du fichier.')
    }

    const data = await response.json()

    members.value.push({
      title: (data.url_image || data.urlimage).split('/').pop(),
      href: data.url_image || data.urlimage,
      id: data.id,
      folder_id: data.folder_id || null,
      folder_name: data.folder_name || null,
      created_at: data.created_at || new Date().toISOString(),
    })

    selectedFile.value = null
    selectedFileFolderId.value = ''
    document.getElementById('file-upload').value = ''
    alert('Fichier ajouté avec succès!')
  } catch (error) {
    console.error(error)
    alert('Une erreur est survenue lors de l\'ajout du fichier.')
  }
}

const addUrlLink = async () => {
  if (!newLinkUrl.value) {
    alert('Veuillez entrer une URL valide.')
    return
  }

  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url: newLinkUrl.value,
        folder_id: selectedUrlFolderId.value || null,
      }),
    })

    if (!response.ok) {
      throw new Error('Erreur lors de l\'ajout du lien URL.')
    }

    const data = await response.json()

    links.value.push({
      url_lien: data.url_lien || newLinkUrl.value,
      href: data.url_lien || newLinkUrl.value,
      id: data.id,
      folder_id: data.folder_id || null,
      folder_name: data.folder_name || null,
      created_at: data.created_at || new Date().toISOString(),
    })

    newLinkUrl.value = '' // Réinitialiser le champ URL
    selectedUrlFolderId.value = ''
    alert('Lien ajouté avec succès!')
  } catch (error) {
    console.error(error)
    alert('Une erreur est survenue lors de l\'ajout du lien.')
  }
}

const deletePdfFile = async (member) => {
  const confirmDelete = confirm('Voulez-vous vraiment supprimer ce fichier ?')

  if (confirmDelete) {
    try {
      const response = await fetch(`https://api.meritemaritime-fnmm.com/member/pdf/${member.id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du fichier.')
      }

      members.value = members.value.filter(m => m.id !== member.id)
      alert('Fichier supprimé avec succès!')
    } catch (error) {
      console.error(error)
      alert('Une erreur est survenue lors de la suppression du fichier.')
    }
  }
}

const deleteUrlLink = async (link) => {
  const confirmDelete = confirm('Voulez-vous vraiment supprimer ce lien ?')

  if (confirmDelete) {
    try {
      const response = await fetch(`https://api.meritemaritime-fnmm.com/member/url/${link.id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du lien.')
      }

      links.value = links.value.filter(l => l.id !== link.id)
      alert('Lien supprimé avec succès!')
    } catch (error) {
      console.error(error)
      alert('Une erreur est survenue lors de la suppression du lien.')
    }
  }
}
</script>

